import { AbstractConnectorArguments, ConnectorUpdate } from '@web3-react/types';
import { AbstractConnector } from '@web3-react/abstract-connector';
import detectEthereumProvider from '@metamask/detect-provider';
import warning from 'tiny-warning';

import { SendReturnResult, SendReturn, Send, SendOld } from './types';

interface MetaMaskEthereumProvider {
    isMetaMask?: boolean;
    send: unknown;
    enable: () => Promise<string[]>;
    once(eventName: string | symbol, listener: (...args: any[]) => void): this;
    on(eventName: string | symbol, listener: (...args: any[]) => void): this;
    off(eventName: string | symbol, listener: (...args: any[]) => void): this;
    addListener(eventName: string | symbol, listener: (...args: any[]) => void): this;
    removeListener(eventName: string | symbol, listener: (...args: any[]) => void): this;
    removeAllListeners(event?: string | symbol): this;
}

declare global {
    interface Window {
        ethereum?: MetaMaskEthereumProvider;
    }
}

function parseSendReturn(sendReturn: SendReturnResult | SendReturn): any {
    return sendReturn.hasOwnProperty('result') ? sendReturn.result : sendReturn;
}

export class NoEthereumProviderError extends Error {
    public constructor() {
        super();
        this.name = this.constructor.name;
        this.message = 'No Ethereum provider was found on window.NaboxWallet.';
    }
}

export class UserRejectedRequestError extends Error {
    public constructor() {
        super();
        this.name = this.constructor.name;
        this.message = 'The user rejected the request.';
    }
}

export class MetamaskConnector extends AbstractConnector {
    constructor(kwargs: AbstractConnectorArguments) {
        super(kwargs);

        this.handleNetworkChanged = this.handleNetworkChanged.bind(this);
        this.handleChainChanged = this.handleChainChanged.bind(this);
        this.handleAccountsChanged = this.handleAccountsChanged.bind(this);
        this.handleClose = this.handleClose.bind(this);
    }

    private handleChainChanged(chainId: string | number): void {
        this.emitUpdate({ chainId, provider: window.ethereum });
    }

    private handleAccountsChanged(accounts: string[]): void {
        if (accounts.length === 0) {
            this.emitDeactivate();
        } else {
            this.emitUpdate({ account: accounts[0] });
        }
    }

    private handleClose(code: number, reason: string): void {
        this.emitDeactivate();
    }

    private handleNetworkChanged(networkId: string | number): void {
        this.emitUpdate({ chainId: networkId, provider: window.ethereum });
    }

    public async getMetamaskProvider(): Promise<MetaMaskEthereumProvider | null> {
        return await detectEthereumProvider({
            mustBeMetaMask: true,
        });
    }

    public async activate(): Promise<ConnectorUpdate> {
        const metamaskProvider = await this.getMetamaskProvider();

        if (!metamaskProvider) {
            throw new NoEthereumProviderError();
        }

        if (metamaskProvider.on) {
            metamaskProvider.on('chainChanged', this.handleChainChanged);
            metamaskProvider.on('accountsChanged', this.handleAccountsChanged);
            metamaskProvider.on('close', this.handleClose);
            metamaskProvider.on('networkChanged', this.handleNetworkChanged);
        }

        // try to activate + get account via eth_requestAccounts
        let account;
        try {
            account = await (metamaskProvider.send as Send)('eth_requestAccounts').then(
                (sendReturn) => parseSendReturn(sendReturn)[0]
            );
        } catch (error) {
            if ((error as any).code === 4001) {
                throw new UserRejectedRequestError();
            }
            warning(
                false,
                'eth_requestAccounts was unsuccessful, falling back to enable'
            );
        }

        // if unsuccessful, try enable
        if (!account) {
            // if enable is successful but doesn't return accounts, fall back to getAccount (not happy i have to do this...)
            account = await metamaskProvider
                .enable()
                .then((sendReturn) => sendReturn && parseSendReturn(sendReturn)[0]);
        }

        return { provider: metamaskProvider, ...(account ? { account } : {}) };
    }

    public async getProvider(): Promise<any> {
        return await this.getMetamaskProvider();
    }

    public async getChainId(): Promise<number | string> {
        const metamaskProvider = await this.getMetamaskProvider();
        if (!metamaskProvider) {
            throw new NoEthereumProviderError();
        }

        let chainId;
        try {
            chainId = await (metamaskProvider.send as Send)('eth_chainId').then(
                parseSendReturn
            );
        } catch {
            warning(false, 'eth_chainId was unsuccessful, falling back to net_version');
        }

        if (!chainId) {
            try {
                chainId = await (metamaskProvider.send as Send)('net_version').then(
                    parseSendReturn
                );
            } catch {
                warning(
                    false,
                    'net_version was unsuccessful, falling back to net version v2'
                );
            }
        }

        if (!chainId) {
            try {
                chainId = parseSendReturn(
                    (metamaskProvider.send as SendOld)({ method: 'net_version' })
                );
            } catch {
                warning(
                    false,
                    'net_version v2 was unsuccessful, falling back to manual matches and static properties'
                );
            }
        }

        if (!chainId) {
            if ((metamaskProvider as any).isDapper) {
                chainId = parseSendReturn(
                    (metamaskProvider as any).cachedResults.net_version
                );
            } else {
                chainId =
                    (metamaskProvider as any).chainId ||
                    (metamaskProvider as any).netVersion ||
                    (metamaskProvider as any).networkVersion ||
                    (metamaskProvider as any)._chainId;
            }
        }

        return chainId;
    }

    public async getAccount(): Promise<null | string> {
        const metamaskProvider = await this.getMetamaskProvider();

        if (!metamaskProvider) {
            throw new NoEthereumProviderError();
        }

        let account;
        try {
            account = await (metamaskProvider.send as Send)('eth_accounts').then(
                (sendReturn) => parseSendReturn(sendReturn)[0]
            );
        } catch {
            warning(false, 'eth_accounts was unsuccessful, falling back to enable');
        }

        if (!account) {
            try {
                account = await metamaskProvider
                    .enable()
                    .then((sendReturn) => parseSendReturn(sendReturn)[0]);
            } catch {
                warning(
                    false,
                    'enable was unsuccessful, falling back to eth_accounts v2'
                );
            }
        }

        if (!account) {
            account = parseSendReturn(
                (metamaskProvider.send as SendOld)({ method: 'eth_accounts' })
            )[0];
        }

        return account;
    }

    public async deactivate() {
        const metamaskProvider = await this.getMetamaskProvider();

        if (metamaskProvider && metamaskProvider.removeListener) {
            metamaskProvider.removeListener('chainChanged', this.handleChainChanged);
            metamaskProvider.removeListener(
                'accountsChanged',
                this.handleAccountsChanged
            );
            metamaskProvider.removeListener('close', this.handleClose);
            metamaskProvider.removeListener('networkChanged', this.handleNetworkChanged);
        }
    }

    public async isAuthorized(): Promise<boolean> {
        const metamaskProvider = await this.getMetamaskProvider();

        if (!metamaskProvider) {
            return false;
        }

        try {
            return await (metamaskProvider.send as Send)('eth_accounts').then(
                (sendReturn) => {
                    if (parseSendReturn(sendReturn).length > 0) {
                        return true;
                    } else {
                        return false;
                    }
                }
            );
        } catch {
            return false;
        }
    }
}
